import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Form, { useForm } from "rc-field-form";
import moment from "moment-timezone";
import Modal from "../../../../../components/Modal";
import { Label } from "reactstrap";
import Select from "../../../../../components/Inputs/Select";
import Field from "../../../../../components/Field";
import Button from "../../../../../components/Button";
import { useRetroAdjustmentApi } from "../../../api/useRetroAdjustmentApi";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { renderSettingsResourceLabel } from "../../../../../utils/helpers/settings";

function RetroAdjustementModal({ visible, close, selected }) {
    const { t } = useTranslation();
    const [form] = useForm();
    const dateFormat = useCompanyDateFormat();
    const { create, loading } = useRetroAdjustmentApi();
    const lockedPayGroupItems = useMemo(() => {
        return selected?.payGroupItems?.filter((item) => item.status === "locked");
    }, [selected?.payGroupItems]);

    const cancel = useCallback(() => {
        close();
        form.resetFields();
    }, [close, form]);

    const onSuccess = useCallback((values) => {
        selected?.mutateRetroAdjustment((prev) => {
            return [...(prev || []), values];
        }, { revalidate: false });
        selected?.refetchTimeSheets();
        cancel();
    }, [cancel, selected]);

    const onFinish = useCallback((values) => {
        const data = {
            user: selected?.user?.id,
            payPeriod: {
                payGroup: selected?.user?.payGroup?.id,
                payGroupItem: values?.period
            }
        };
        create(data, onSuccess);
    }, [create, onSuccess, selected?.user?.id, selected?.user?.payGroup?.id]);

    useEffect(() => {
        if (lockedPayGroupItems?.length === 1) {
            form.setFieldValue("period", lockedPayGroupItems?.[0]?.id);
        }
    }, [lockedPayGroupItems, form]);

    return (
        <Modal
            title={t("create")}
            isOpen={visible}
            centered
        >
            <Form form={form} onFinish={onFinish}>
                <div className="d-flex flex-column mb-4">
                    <Label>
                        {t("pay-group")}
                        <span className="text-danger mx-1">*</span>
                    </Label>
                    <Select disabled={true} value={selected?.user?.payGroup?.id}>
                        <Select.Option value={selected?.user?.payGroup?.id}>
                            {renderSettingsResourceLabel(selected?.user?.payGroup)}
                        </Select.Option>
                    </Select>
                </div>

                <Field
                    name="period"
                    label={t("pick-pay-period")}
                    rules={[
                        {
                            required: true,
                            message: t("period-required")
                        }
                    ]}
                >
                    <Select placeholder={t("period")}>
                        {lockedPayGroupItems?.map((item) => (
                            <Select.Option value={item.id} key={item?.id}>
                                {moment.parseZone(item.periodStartDate).format(dateFormat)}
                                {" - "}
                                {moment.parseZone(item.periodEndDate).format(dateFormat)}
                            </Select.Option>
                        ))}
                    </Select>
                </Field>

                <div className="d-flex flex-row gap-2 justify-content-end">
                    <Button onClick={cancel} type="button" disabled={loading}>
                        {t("cancel")}
                    </Button>
                    <Button color="primary" disabled={loading} loading={loading}>
                        {t("create")}
                    </Button>
                </div>
            </Form>
        </Modal>
    );
}

export default RetroAdjustementModal;
